import type { MutationDefinition } from '@reduxjs/toolkit/query';
import type { createAuthenticatedBaseQuery } from '../../../../../services/shared';
import { encounterApi } from '../../../../../services/EncounterService/encounterApi';
import type {
  CreateMedicalRecordsRequestArgs,
  CreateMedicalRecordsRequestResponse,
  GetMyMedicalRecordsRequestsArgs,
  MedicalRecordsEncounterResponse,
  MedicalRecordsQueueResponse,
  SearchMedicalRecordsEncountersArgs,
  CreateMedicalRecordsDemoArgs,
  MedicalRecordsDemoResponse,
  MedicalRecordsDocumentScope,
  MedicalRecordsDocumentsResponse,
  DownloadMedicalRecordsDocumentArgs,
  RetryMedicalRecordsArgs,
} from './medicalRecordsViews';

const documentPath = (requestId: string) => `/medical-records/requests/${encodeURIComponent(requestId)}/documents`;

const saveVerifiedDownload = (content: Blob, fileName: string): void => {
  const url = URL.createObjectURL(content);
  const link = document.createElement('a');
  try {
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
  } finally {
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};

type DocumentDownloadQuery = NonNullable<MutationDefinition<
  DownloadMedicalRecordsDocumentArgs, ReturnType<typeof createAuthenticatedBaseQuery>, never, null
>['queryFn']>;

const downloadVerifiedDocument: DocumentDownloadQuery = async (
  { requestId, documentId, fileName, ...params }, api, _options, baseQuery
) => {
  const suffix = documentId === undefined ? 'archive' : `${encodeURIComponent(documentId)}/content`;
  const contentTypes = documentId === undefined ? ['application/zip'] : ['application/pdf', 'application/octet-stream'];
  const result = await baseQuery({
    url: `${documentPath(requestId)}/${suffix}`, params,
    responseHandler: (response) => response.ok ? response.blob() : response.json(),
    cache: 'no-store',
  });
  if (result.error) return { error: result.error };
  const content = result.data;
  if (api.signal.aborted) return { error: { status: 'CUSTOM_ERROR', error: 'Download canceled.' } };
  if (!(content instanceof Blob) || !contentTypes.includes(content.type)) {
    return { error: { status: 'CUSTOM_ERROR', error: 'The requested document download was not returned.' } };
  }
  try {
    saveVerifiedDownload(content, fileName);
    // File bytes and object URLs never enter the Redux cache.
    return { data: null };
  } catch {
    return { error: { status: 'CUSTOM_ERROR', error: 'The browser could not save this download.' } };
  }
};

/**
 * Medical Records feature endpoints, injected into the single Encounters base api (contract:
 * Services/Encounters/Features/MedicalRecords/Endpoints/MedicalRecordsEndpoints.cs). The create
 * mutation carries its Idempotency-Key as a request header, not a body field — the caller
 * retains one key for retries of the accepted request and replaces it after that request becomes
 * terminal (see `useMedicalRecords`).
 */
export const medicalRecordsEndpoints = encounterApi.injectEndpoints({
  endpoints: (builder) => ({
    searchMedicalRecordsEncounters: builder.query<
      MedicalRecordsEncounterResponse,
      SearchMedicalRecordsEncountersArgs
    >({
      query: (body) => ({
        url: '/medical-records/encounters/search', method: 'POST', body,
      }),
    }),

    getMyMedicalRecordsRequests: builder.query<
      MedicalRecordsQueueResponse,
      GetMyMedicalRecordsRequestsArgs
    >({
      query: (body) => ({
        url: '/medical-records/requests/mine/search', method: 'POST', body,
      }),
      providesTags: ['MedicalRecordsQueue'],
    }),

    createMedicalRecordsRequest: builder.mutation<
      CreateMedicalRecordsRequestResponse,
      CreateMedicalRecordsRequestArgs
    >({
      query: ({ idempotencyKey, ...body }) => ({
        url: '/medical-records/requests',
        method: 'POST',
        body,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: ['MedicalRecordsQueue'],
    }),
    retryMedicalRecordsRequest: builder.mutation<CreateMedicalRecordsRequestResponse, RetryMedicalRecordsArgs>({
      query: ({ requestId, ...body }) => ({
        url: `/medical-records/requests/${encodeURIComponent(requestId)}/retry`, method: 'POST', body,
      }),
      invalidatesTags: ['MedicalRecordsQueue'],
    }),
    createMedicalRecordsDemo: builder.mutation<MedicalRecordsDemoResponse, CreateMedicalRecordsDemoArgs>({
      query: ({ idempotencyKey, ...body }) => ({
        url: '/medical-records/demo/requests', method: 'POST', body,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: ['MedicalRecordsQueue'],
    }),
    prepareMedicalRecordsDemo: builder.mutation<MedicalRecordsDemoResponse, MedicalRecordsDocumentScope>({
      query: ({ requestId, ...params }) => ({
        url: `/medical-records/demo/requests/${encodeURIComponent(requestId)}/prepare`, method: 'POST', params,
      }),
      invalidatesTags: ['MedicalRecordsQueue'],
    }),
    getMedicalRecordsDocuments: builder.query<MedicalRecordsDocumentsResponse, MedicalRecordsDocumentScope>({
      query: ({ requestId, ...params }) => ({ url: documentPath(requestId), params }),
      keepUnusedDataFor: 0,
    }),
    downloadMedicalRecordsDocument: builder.mutation<null, DownloadMedicalRecordsDocumentArgs>({
      queryFn: downloadVerifiedDocument,
    }),
  }),
  overrideExisting: false,
});

export const {
  useSearchMedicalRecordsEncountersQuery,
  useGetMyMedicalRecordsRequestsQuery,
  useCreateMedicalRecordsRequestMutation,
  useRetryMedicalRecordsRequestMutation,
  useCreateMedicalRecordsDemoMutation,
  usePrepareMedicalRecordsDemoMutation,
  useGetMedicalRecordsDocumentsQuery,
  useDownloadMedicalRecordsDocumentMutation,
} = medicalRecordsEndpoints;
